import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { useParams } from "react-router-dom";
import { Link, Navigate } from "@/i18n/Link";
import { ArrowRight, Phone, MapPin, Wrench, Package, ShieldCheck, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd, serviceJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE, SITE_PHONE_DISPLAY, SITE_URL } from "@/routes-data";
import { getCityL } from "@/locations/cities";
import { getProducts } from "@/locations/products";
import { localizedAssetUrl } from "@/i18n/assets";
import { useLocale, useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    home: "Home",
    locations: "Locations",
    trailerRepair: "Trailer Repair",
    repairDesc: "Bearing repacks, brake service, hub replacements, spindle work, and full axle replacements on light-duty trailers up to 10,000 lb capacity.",
    axlesForSale: "Axles for Sale",
    axlesDesc: "Dexter Group OEM axles in stock - 1,000 to 10,000 lb capacity, idler or brake configurations, with hubs and running gear ready to install.",
    trailerAxles: "Trailer Axles",
    trailerAxlesDesc: "Spec'd, sourced, and built. We carry standard configurations and build custom axles to your exact length, capacity, hub face, and brake type.",
    trailerParts: "Trailer Parts",
    partsDesc: "Hubs, bearings, brake assemblies, lights, jacks, couplers, suspension parts, and electrical components - walk-in counter open during business hours.",
    title: (name: string, state: string) => `Trailer Axles & Repair in ${name}, ${state} - Dexter Distributor`,
    description: (name: string, state: string, dist: number, dir: string) => `Trailer axles, parts, and repair for ${name}, ${state} trailer owners - ${dist} miles ${dir} of our Elkhart shop. Authorized Dexter distributor. Call ${SITE_PHONE_DISPLAY}.`,
    serviceName: (name: string, state: string) => `Trailer Axles, Parts & Repair - ${name}, ${state}`,
    orgName: (name: string, state: string) => `Axle Inc. - Serving ${name}, ${state}`,
    serving: (county: string) => `Serving ${county}`,
    h1: (name: string, state: string) => `Trailer Axles, Parts & Repair in ${name}, ${state}`,
    schedule: "Schedule service",
    imgAlt: (name: string, state: string) => `Axle Inc. trailer service for ${name}, ${state}`,
    whatWeSee: (name: string) => `What we see from ${name} customers`,
    quickFacts: "Quick facts",
    distShop: "Distance from our shop",
    milesDir: (d: number, dir: string) => `${d} miles ${dir}`,
    routes: "Routes to us",
    county: "County",
    zipCodes: "ZIP codes served",
    areasCovered: "Areas covered",
    whyChoose: (name: string) => `Why ${name} trailer owners choose Axle Inc.`,
    whySub: (state: string) => `Sixty-plus years of combined experience servicing the ${state} trailer market - recreational, agricultural, and commercial.`,
    offer: (name: string) => `What we offer to ${name} customers`,
    offerSub: "Trailer repair, axles for sale, trailer axles, and parts - under one roof, with the inventory and expertise to get your trailer back on the road.",
    allRepair: "All repair services",
    emergency: (name: string) => `Emergency & same-day trailer repair for ${name}`,
    browse: "Browse parts catalog",
    rebates: "Dexter rebates",
    dedicated: (name: string) => `Dedicated ${name} pages for each service`,
    dediSub: (name: string) => `Detailed information for ${name} customers on each of our four core offerings - written specifically for the trailer market in your area.`,
    prodLabel: (label: string, name: string, state: string) => `${label} in ${name}, ${state}`,
    faq: (name: string) => `${name} - frequently asked questions`,
    ready: (name: string) => `Ready to get your ${name} trailer serviced?`,
    readySub: "Schedule online, call the shop, or drop in for parts. We'll be straight with you about what's needed and what isn't.",
    scheduleOnline: "Schedule online",
    otherCities: "Other cities we serve",
    viewAll: "View all 20 service areas",
    footerSub: " across northern Indiana and southwest Michigan."
  },
  es: {
    home: "Inicio",
    locations: "Ubicaciones",
    trailerRepair: "Reparación de remolques",
    repairDesc: "Reempaquetado de rodamientos, servicio de frenos, reemplazo de mazas, trabajo en ejes y reemplazo completo de ejes para remolques ligeros de hasta 10,000 libras de capacidad.",
    axlesForSale: "Ejes a la venta",
    axlesDesc: "Ejes OEM de Dexter Group en existencia - capacidad de 1,000 a 10,000 libras, configuraciones con o sin frenos, con mazas y tren de rodaje listos para instalar.",
    trailerAxles: "Ejes para remolque",
    trailerAxlesDesc: "Especificados, obtenidos y construidos. Contamos con configuraciones estándar y construimos ejes personalizados según su longitud, capacidad, cara de la maza y tipo de freno exactos.",
    trailerParts: "Piezas para remolque",
    partsDesc: "Mazas, rodamientos, conjuntos de frenos, luces, gatos, acopladores, piezas de suspensión y componentes eléctricos - mostrador abierto durante el horario comercial.",
    title: (name: string, state: string) => `Ejes y reparación de remolques en ${name}, ${state} - Distribuidor Dexter`,
    description: (name: string, state: string, dist: number, dir: string) => `Ejes, piezas y reparación de remolques para propietarios en ${name}, ${state} - a ${dist} millas al ${dir} de nuestro taller en Elkhart. Distribuidor autorizado de Dexter. Llame al ${SITE_PHONE_DISPLAY}.`,
    serviceName: (name: string, state: string) => `Ejes, piezas y reparación de remolques - ${name}, ${state}`,
    orgName: (name: string, state: string) => `Axle Inc. - Sirviendo a ${name}, ${state}`,
    serving: (county: string) => `Sirviendo al condado de ${county}`,
    h1: (name: string, state: string) => `Ejes, piezas y reparación de remolques en ${name}, ${state}`,
    schedule: "Programar servicio",
    imgAlt: (name: string, state: string) => `Servicio de remolques de Axle Inc. para ${name}, ${state}`,
    whatWeSee: (name: string) => `Lo que vemos de los clientes de ${name}`,
    quickFacts: "Datos rápidos",
    distShop: "Distancia desde nuestro taller",
    milesDir: (d: number, dir: string) => `${d} millas al ${dir}`,
    routes: "Rutas hacia nosotros",
    county: "Condado",
    zipCodes: "Códigos postales cubiertos",
    areasCovered: "Áreas cubiertas",
    whyChoose: (name: string) => `Por qué los propietarios de remolques en ${name} eligen Axle Inc.`,
    whySub: (state: string) => `Más de sesenta años de experiencia combinada sirviendo al mercado de remolques en ${state}: recreativo, agrícola y comercial.`,
    offer: (name: string) => `Lo que ofrecemos a los clientes de ${name}`,
    offerSub: "Reparación de remolques, ejes a la venta, ejes para remolques y piezas: todo bajo un mismo techo, con el inventario y la experiencia necesarios para que su remolque vuelva a la carretera.",
    allRepair: "Todos los servicios de reparación",
    emergency: (name: string) => `Reparación de emergencia y el mismo día para remolques en ${name}`,
    browse: "Explorar catálogo de piezas",
    rebates: "Reembolsos de Dexter",
    dedicated: (name: string) => `Páginas dedicadas para cada servicio en ${name}`,
    dediSub: (name: string) => `Información detallada para los clientes de ${name} sobre cada una de nuestras cuatro ofertas principales, escrita específicamente para el mercado de remolques de su área.`,
    prodLabel: (label: string, name: string, state: string) => `${label} en ${name}, ${state}`,
    faq: (name: string) => `Preguntas frecuentes de ${name}`,
    ready: (name: string) => `¿Listo para dar servicio a su remolque en ${name}?`,
    readySub: "Programe en línea, llame al taller o visítenos por piezas. Seremos honestos con usted sobre lo que se necesita y lo que no.",
    scheduleOnline: "Programar en línea",
    otherCities: "Otras ciudades que servimos",
    viewAll: "Ver las 20 áreas de servicio",
    footerSub: " en el norte de Indiana y el suroeste de Michigan."
  }
};

export default function CityPage() {
  const { slug } = useParams<{ slug: string }>();
  const { locale } = useLocale();
  const t = useCopy(COPY);
  const city = slug ? getCityL(slug, locale) : undefined;
  if (!city) {
    return <Navigate to="/locations/" replace />;
  }
  const path = `/locations/${city.slug}/`;
  const image = `${locale === "es" ? "/es" : ""}/locations/${city.slug}.svg`;
  const trail = [
    { name: t.home, path: "/" },
    { name: t.locations, path: "/locations/" },
    { name: `${city.name}, ${city.state}`, path },
  ];
  const title = t.title(city.name, city.state);
  const description = t.description(city.name, city.stateName, city.distance, city.direction);
  
  const SHARED_SERVICES = [
    { title: t.trailerRepair, desc: t.repairDesc },
    { title: t.axlesForSale, desc: t.axlesDesc },
    { title: t.trailerAxles, desc: t.trailerAxlesDesc },
    { title: t.trailerParts, desc: t.partsDesc },
  ];

  return (
    <PageLayout>
      <Seo
        title={title}
        description={description}
        path={path}
        image={image}
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          serviceJsonLd({
            name: t.serviceName(city.name, city.state),
            description,
            path,
            serviceType: t.trailerRepair,
          }),
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "@id": `${SITE_URL}${path}#localbusiness`,
            name: t.orgName(city.name, city.state),
            description,
            url: `${SITE_URL}${path}`,
            telephone: SITE_PHONE,
            image: `${SITE_URL}${image}`,
            priceRange: "$$",
            address: {
              "@type": "PostalAddress",
              streetAddress: "29473 US-33",
              addressLocality: "Elkhart",
              addressRegion: "IN",
              postalCode: "46517",
              addressCountry: "US",
            },
            areaServed: {
              "@type": "City",
              name: city.name,
              address: {
                "@type": "PostalAddress",
                addressLocality: city.name,
                addressRegion: city.state,
                postalCode: city.zips.split(",")[0].trim(),
                addressCountry: "US",
              },
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: city.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />

      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">
            <MapPin className="inline-block h-3.5 w-3.5 mr-1.5 -mt-0.5" />
            {t.serving(city.county)}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-4xl">
            {t.h1(city.name, city.state)}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/85 max-w-3xl leading-relaxed">
            {city.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.schedule} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12 md:py-16">
        <img
          src={localizedAssetUrl(`/locations/${city.slug}.svg`, locale)}
          alt={t.imgAlt(city.name, city.stateName)}
          width={1200}
          height={630}
          className="w-full rounded-md border border-border bg-muted"
          loading="eager"
        />
      </section>

      <section className="container mx-auto px-4 pb-12 md:pb-16">
        <div className="grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2 space-y-6 leading-relaxed">
            <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
              {t.whatWeSee(city.name)}
            </h2>
            <p className="text-muted-foreground">{city.localContext}</p>
            <p className="text-muted-foreground">{city.midContent}</p>
          </div>
          <aside className="bg-card border border-border rounded-md p-6 space-y-4 self-start">
            <h3 className="font-semibold text-lg">{t.quickFacts}</h3>
            <dl className="text-sm space-y-3">
              <div>
                <dt className="text-muted-foreground">{t.distShop}</dt>
                <dd className="font-semibold">{t.milesDir(city.distance, city.direction)}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.routes}</dt>
                <dd className="font-semibold">{city.highways}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.county}</dt>
                <dd className="font-semibold">{city.county}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.zipCodes}</dt>
                <dd className="font-semibold">{city.zips}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t.areasCovered}</dt>
                <dd className="font-semibold">{city.neighborhoods}</dd>
              </div>
            </dl>
          </aside>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-3xl">
            {t.whyChoose(city.name)}
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-10">
            {t.whySub(city.stateName)}
          </p>
          <div className="grid gap-6 md:grid-cols-2">
            {city.whyChoose.map((reason, i) => (
              <div key={i} className="flex gap-4 bg-card border border-border rounded-md p-5">
                <div className="shrink-0 h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold">
                  {i + 1}
                </div>
                <p className="text-sm leading-relaxed">{reason}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-3xl">
          {t.offer(city.name)}
        </h2>
        <p className="text-muted-foreground max-w-3xl mb-10">
          {t.offerSub}
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          {SHARED_SERVICES.map((s, i) => {
            const Icon = [Wrench, Package, ShieldCheck, Clock][i];
            return (
              <article key={s.title} className="border border-border rounded-md p-6 bg-card">
                <Icon className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </article>
            );
          })}
        </div>
        <div className="mt-10 flex flex-wrap gap-3">
          <Button asChild variant="outline">
            <Link to="/services/axle-repair/">{t.allRepair} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/services/emergency-same-day-trailer-repair/">{t.emergency(city.name)} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/products/">{t.browse} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/dexter/">{t.rebates} <ArrowRight className="ml-1.5 h-4 w-4" /></Link>
          </Button>
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-3 max-w-3xl">
            {t.dedicated(city.name)}
          </h2>
          <p className="text-muted-foreground max-w-3xl mb-8">
            {t.dediSub(city.name)}
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            {getProducts(locale).map((p) => (
              <Link
                key={p.key}
                to={`/locations/${city.slug}/${p.slug}/`}
                className="group bg-card border border-border hover:border-primary hover:shadow-md rounded-md p-5 transition-all"
              >
                <h3 className="font-semibold mb-1 flex items-center justify-between gap-3">
                  <span>{t.prodLabel(p.label, city.name, city.state)}</span>
                  <ArrowRight className="h-4 w-4 text-primary shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </h3>
                <p className="text-sm text-muted-foreground line-clamp-2">{p.metaDesc(city.name, city.state)}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 pb-16 md:pb-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">
          {t.faq(city.name)}
        </h2>
        <div className="divide-y divide-border border-y border-border">
          {city.faqs.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="cursor-pointer list-none flex justify-between items-start gap-4 font-medium">
                {f.q}
                <span className="text-primary text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <ShieldCheck className="h-8 w-8 mt-1 shrink-0" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">{t.ready(city.name)}</h2>
              <p className="mt-1 text-primary-foreground">{t.readySub}</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-white text-primary-strong hover:bg-white/90">{t.scheduleOnline}</Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/40 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12">
        <h2 className="text-lg font-semibold mb-4">{t.otherCities}</h2>
        <p className="text-sm text-muted-foreground">
          <Link to="/locations/" className="text-primary-strong hover:underline underline-offset-4">{t.viewAll}</Link>{t.footerSub}
        </p>
      </section>
    </PageLayout>
  );
}
